// warehouse/examAnalyticsMerge.js — RPW T5 (P3): LA FUSIÓN, PURA.
//
// EL HALLAZGO (F1 del smoke): una entrega de examen hecha en el cel no aparecía
// en Analytics en la PRIMERA visita — aparecía al salir y volver. No era cache:
// lo que cambiaba en medio era el SERVIDOR. El acta se guarda local al instante,
// pero el cartero la sube con debounce, así que la primera visita pedía
// `/analytics/document/{id}` antes de que el servidor la tuviera.
//
// El principio firmado: LO QUE EL CEL YA SABE, EL CEL LO MUESTRA.
//
// Este módulo NO importa nada: es aritmética sobre dos objetos. Vive aparte de
// `screenSources` a propósito — el lector de la bodega necesita SQLite y la
// pantalla necesita esta fusión, así que separarlos permite probar la pantalla con
// la fusión DE VERDAD y sólo el lector mockeado. La regla del repo (una sola
// fórmula de porcentaje) se respeta calcando `engine_core.session_score`.

// El redondeo del servidor es el de Python: mitades AL PAR ("banquero"), no
// media-arriba como `Math.round`. Sin esto, una exactitud de 12.5 saldría 13 aquí
// y 12 en cuanto el cartero subiera el acta — el número brincaría al sincronizar,
// que es justo lo que hace visible una fila que debe ser indistinguible.
export function roundHalfEven(value) {
  const piso = Math.floor(value);
  const resto = value - piso;
  if (resto > 0.5) return piso + 1;
  if (resto < 0.5) return piso;
  return piso % 2 === 0 ? piso : piso + 1;
}

// La ÚNICA fórmula de porcentaje del producto (engine_core.py:635-640).
export function sessionScore(correct, total) {
  return roundHalfEven((correct / Math.max(total, 1)) * 100);
}

/**
 * El bloque `exam` del detalle por documento, con las entregas locales fusionadas
 * dentro: mismo shape, mismos campos, indistinguibles a la vista.
 *
 * PURA y con IDENTIDAD: sin entregas devuelve el objeto del servidor TAL CUAL (la
 * misma referencia), así que el camino web —y el del cel ya sincronizado— no
 * cambian ni un byte. Con entregas recalcula los agregados sobre la UNIÓN, que es
 * lo que los vuelve honestos: el "best" se corona bien y el conteo cuadra.
 *
 * `topic_accuracy` se fusiona por SUMA sobre lo que el servidor ya agregó
 * (correctos y total por tema son aditivos), así que el desglose incluye la
 * entrega local sin necesitar las filas crudas del servidor, que el payload no
 * trae.
 *
 * Con `exam` nulo (el servidor no tiene NINGÚN examen de este documento) se arma
 * el bloque completo desde lo local: una entrega recién hecha se pinta sola.
 *
 * El orden se decide por INSTANTE parseado, no comparando strings: el acta local
 * sella con `...Z` y el servidor con `+00:00`, y comparar esas dos formas como
 * texto ordena por el signo del huso en vez de por la hora.
 */
export function mergeExamDeliveries(exam, deliveries) {
  if (!deliveries || deliveries.length === 0) return exam;

  const servidor = exam || {
    sessions: [], best_score: 0, average_score: 0, total_exams: 0,
    topic_accuracy: [],
  };
  const instante = (fila) => {
    const t = Date.parse(fila?.date || '');
    return Number.isFinite(t) ? t : 0;
  };
  const sessions = [...deliveries.map((d) => d.row), ...(servidor.sessions || [])]
    .sort((a, b) => instante(b) - instante(a));

  const scores = sessions.map((s) => s.score ?? 0);

  // Los conteos por tema del servidor, más los de cada entrega local.
  const porTema = new Map();
  (servidor.topic_accuracy || []).forEach((t) => {
    porTema.set(t.topic, { correct: t.correct || 0, total: t.total || 0 });
  });
  deliveries.forEach((d) => {
    (d.results_summary || []).forEach((r) => {
      if (!r || !r.topic) return;
      const acc = porTema.get(r.topic) || { correct: 0, total: 0 };
      acc.total += 1;
      if (r.is_correct) acc.correct += 1;
      porTema.set(r.topic, acc);
    });
  });
  const topic_accuracy = [...porTema.entries()]
    .map(([topic, v]) => ({
      topic, correct: v.correct, total: v.total,
      accuracy: sessionScore(v.correct, v.total),
    }))
    // Peor primero, igual que el servidor.
    .sort((a, b) => a.accuracy - b.accuracy);

  return {
    ...servidor,
    sessions,
    best_score: scores.length ? Math.max(...scores) : 0,
    average_score: scores.length
      ? roundHalfEven(scores.reduce((a, b) => a + b, 0) / scores.length)
      : 0,
    total_exams: sessions.length,
    topic_accuracy,
  };
}
